import { useState } from 'react';
import {
  DIAS_VENCIMENTO_SALDO,
  DIAS_VENCIMENTO_SINAL,
  DOCUMENTOS,
  MAX_DIAS_PRIMEIRO_VENCIMENTO,
  MAX_PARCELAS_SALDO,
  MIN_PARCELAS_CARNE,
  cobrancaJaPaga,
  formatarBRL,
  formatarDataCurta,
  formatarPercentual,
  hojeNaOperacao,
  partesDaData,
  percentualDoSinal,
  resumoDoCarne,
  somarDias,
  validarPrimeiroVencimento,
  valoresVigentes,
  vencimentosDoCarne,
  type Candidato,
  type Cobranca,
  type MarcoId,
  type ModalidadeCobranca,
  type TipoCobranca,
  type TipoDocumento,
} from '@aponte/shared';
import type { DocumentoNaFicha } from '../../api/cliente.js';
import { useSessao } from '../../autenticacao/ContextoDeSessao.js';

interface Props {
  candidato: Candidato;
  cobrancas: Cobranca[];
  documentos: DocumentoNaFicha[];
  ocupado: boolean;
  aoEmitir: (tipo: TipoDocumento) => void;
  aoRegistrarAssinatura: (documento: DocumentoNaFicha) => void;
  /** `carne` só: quantidade de boletos e data da primeira parcela, ambas do operador. */
  aoCobrar: (
    tipo: TipoCobranca,
    modalidade: ModalidadeCobranca,
    carne?: { parcelas: number; primeiroVencimento: string },
  ) => void;
  aoDecidirCuradoria: (aprovado: boolean) => void;
  aoMarcarAceite: () => void;
  aoRegistrarOnboarding: () => void;
}

/**
 * A esteira de A Ponte, na ordem em que a operação executa.
 *
 * Cada passo só libera quando o anterior tem seu marco carimbado — a mesma regra que a
 * API aplica do lado do servidor. Aqui a trava é conforto do operador; lá é garantia.
 *
 * Emissão e assinatura são passos **separados** de propósito: gerar o PDF não prova que
 * o Proponente concordou com nada, e é o documento assinado que move o contrato adiante.
 */
export function Esteira({
  candidato,
  cobrancas,
  documentos,
  ocupado,
  aoEmitir,
  aoRegistrarAssinatura,
  aoCobrar,
  aoDecidirCuradoria,
  aoMarcarAceite,
  aoRegistrarOnboarding,
}: Props) {
  const { temPapel } = useSessao();
  const valores = valoresVigentes(candidato);
  const { sinal, saldo } = valores;
  const percentualSinal = formatarPercentual(percentualDoSinal(valores));
  const percentualSaldo = formatarPercentual(100 - percentualDoSinal(valores));
  const marcos = candidato.marcos;
  /*
   * O que o operador libera no link do saldo. Não é a quantidade de parcelas: essa quem
   * escolhe é o pagador, na tela do checkout. Aqui se decide apenas se o parcelamento
   * está disponível para ele.
   */
  const [modalidadeDoSaldo, setModalidadeDoSaldo] = useState<ModalidadeCobranca>('avista');
  /** Quantos boletos o carnê terá. Aqui, ao contrário do parcelado, quem decide é o operador. */
  const [parcelasDoCarne, setParcelasDoCarne] = useState<number>(MAX_PARCELAS_SALDO);
  const opcoesDeParcelas = Array.from(
    { length: MAX_PARCELAS_SALDO - MIN_PARCELAS_CARNE + 1 },
    (_, i) => MIN_PARCELAS_CARNE + i,
  );

  /*
   * Data da primeira parcela. Começa no prazo contratual do saldo, que é o que valeria sem
   * ninguém escolher nada — o campo é para negociar outra, não para obrigar a digitar.
   */
  const hoje = hojeNaOperacao();
  const [primeiroVencimento, setPrimeiroVencimento] = useState<string>(() =>
    somarDias(hoje, DIAS_VENCIMENTO_SALDO),
  );
  const problemaNaData = validarPrimeiroVencimento(primeiroVencimento, hoje);
  const vencimentos = problemaNaData ? [] : vencimentosDoCarne(primeiroVencimento, parcelasDoCarne);

  const cobrancaDe = (tipo: TipoCobranca) =>
    cobrancas.filter((c) => c.tipo === tipo).at(-1) ?? null;

  /** Versão vigente de cada tipo — a assinatura se registra sobre a mais nova. */
  const documentoDe = (tipo: TipoDocumento): DocumentoNaFicha | null =>
    documentos
      .filter((d) => d.tipo === tipo)
      .sort((a, b) => a.versao - b.versao)
      .at(-1) ?? null;

  const podeCurar = temPapel('curadoria');
  const podeOperar = temPapel('closer');

  const passos: Passo[] = [
    {
      nome: 'Carta de Intenções',
      descricao:
        `Emitida com os dados da ficha e o sinal de ${formatarBRL(sinal)} já calculado. ` +
        'A emissão congela os valores do contrato: mudança futura de tabela não afeta mais ' +
        'este candidato.',
      liberado: true,
      marco: 'cartaEmitida',
      acoes: podeOperar ? (
        <>
          <BotaoDeEmissao
            documento={documentoDe('carta')}
            rotulo="Carta"
            ocupado={ocupado}
            aoEmitir={() => aoEmitir('carta')}
          />
          <AvisoDeDivergencia documento={documentoDe('carta')} />
        </>
      ) : null,
    },
    {
      nome: 'Carta assinada de volta',
      descricao:
        'Válida até a decisão do comitê, desde que o sinal seja pago em até ' +
        `${DIAS_VENCIMENTO_SINAL} dias corridos da assinatura (cl. 7.1).`,
      liberado: !!marcos.cartaEmitida,
      marco: 'cartaAssinadaRecebida',
      acoes: podeOperar ? (
        <BotaoDeAssinatura
          documento={documentoDe('carta')}
          ocupado={ocupado}
          aoRegistrar={aoRegistrarAssinatura}
        />
      ) : null,
    },
    {
      nome: `Link do sinal · ${percentualSinal} · ${formatarBRL(sinal)}`,
      descricao:
        'À vista, cartão ou Pix, sem parcelamento. Split de 70% para a Academia aplicado ' +
        'pelo gateway. O pagamento confirmado pelo Asaas move a ficha para curadoria sozinho. ' +
        'Gerar o link fecha o valor do sinal: depois dele, a ficha não aceita mais alteração.',
      liberado: !!marcos.cartaEmitida,
      marco: 'sinalPago',
      acoes: podeOperar ? (
        <BotaoDeCobranca
          cobranca={cobrancaDe('sinal')}
          rotulo="Gerar link do sinal"
          ocupado={ocupado}
          // Sinal é sempre à vista (Carta, cl. 3.2) — não há modalidade a escolher.
          aoCobrar={() => aoCobrar('sinal', 'avista')}
        />
      ) : null,
    },
    {
      nome: 'Curadoria',
      descricao:
        'Decisão de Gilson Rodrigues em até 10 dias úteis contados do sinal compensado. ' +
        'Sem aprovação, o sinal é devolvido integralmente em até 5 dias úteis.',
      liberado: !!marcos.sinalPago,
      marco: 'curadoriaDecidida',
      acoes: podeCurar ? (
        <>
          <button
            className="bt pequeno"
            disabled={ocupado}
            onClick={() => aoDecidirCuradoria(true)}
          >
            Aprovar
          </button>
          <button
            className="bt perigo pequeno"
            disabled={ocupado}
            onClick={() => aoDecidirCuradoria(false)}
          >
            Não aprovar
          </button>
        </>
      ) : (
        <span className="aguardando">Decisão exclusiva do comitê de curadoria.</span>
      ),
    },
    {
      nome: 'Termo de Aceite e Regimento',
      descricao:
        'Emitidos somente após a aprovação. O Regimento Interno é anexo indissociável do Termo.',
      liberado: ['aprovado', 'aceite', 'ativo'].includes(candidato.etapa),
      marco: 'termoEmitido',
      acoes: podeOperar ? (
        <>
          <BotaoDeEmissao
            documento={documentoDe('termo')}
            rotulo="Termo"
            ocupado={ocupado}
            aoEmitir={() => aoEmitir('termo')}
          />
          <BotaoDeEmissao
            documento={documentoDe('regimento')}
            rotulo="Regimento"
            ocupado={ocupado}
            /* O Regimento é anexo do Termo e assina junto: travá-lo pela assinatura do
               Termo evita anexo novo pendurado em contrato já firmado. */
            travadoPor={documentoDe('termo')}
            aoEmitir={() => aoEmitir('regimento')}
          />
          <AvisoDeDivergencia documento={documentoDe('termo')} />
        </>
      ) : null,
    },
    {
      nome: 'Termo assinado e aceite',
      descricao:
        'É o Termo assinado que firma o contrato — emitir o PDF não comprova aceitação. ' +
        'Só depois disso a cadeira pode ser aceita e o saldo, cobrado.',
      liberado: !!marcos.termoEmitido,
      marco: 'termoAssinadoRecebido',
      acoes: podeOperar ? (
        <>
          <BotaoDeAssinatura
            documento={documentoDe('termo')}
            ocupado={ocupado}
            aoRegistrar={aoRegistrarAssinatura}
          />
          {marcos.termoAssinadoRecebido && candidato.etapa === 'aprovado' && (
            <button className="bt pequeno" disabled={ocupado} onClick={aoMarcarAceite}>
              Marcar aceite
            </button>
          )}
        </>
      ) : null,
    },
    {
      nome: `Link do saldo · ${percentualSaldo} · ${formatarBRL(saldo)}`,
      descricao:
        `À vista: cartão, Pix ou boleto. Parcelado: cartão em até ${MAX_PARCELAS_SALDO}x — quem ` +
        `escolhe quantas parcelas é o pagador — ou Pix à vista, no mesmo link. Carnê: boletos ` +
        `mensais na quantidade combinada, e a cadeira só é dada por paga quando a última for ` +
        `confirmada. Mesmo split de 70/30. Vencimento em ${DIAS_VENCIMENTO_SALDO} dias.`,
      liberado: !!marcos.termoAssinadoRecebido,
      marco: 'saldoPago',
      acoes: podeOperar ? (
        <>
          {!cobrancaDe('saldo') && (
            <>
              <label htmlFor="modalidade" style={{ margin: 0 }}>
                Modalidade
              </label>
              <select
                id="modalidade"
                value={modalidadeDoSaldo}
                onChange={(e) => setModalidadeDoSaldo(e.target.value as ModalidadeCobranca)}
                style={{ width: 'auto', padding: '8px 10px', fontSize: 12 }}
              >
                <option value="avista">À vista · cartão, Pix ou boleto</option>
                <option value="parcelado">
                  Parcelado · cartão até {MAX_PARCELAS_SALDO}x, ou Pix à vista
                </option>
                <option value="carne">Carnê · boletos mensais (ou Pix por parcela)</option>
              </select>
              {/*
                A quantidade só aparece no carnê porque só ali ela é do operador. Oferecê-la
                no parcelado daria a impressão de estar fixando algo que o checkout ignora —
                lá quem escolhe é o pagador.
              */}
              {modalidadeDoSaldo === 'carne' && (
                <>
                  <label htmlFor="parcelas-carne" style={{ margin: 0 }}>
                    Boletos
                  </label>
                  <select
                    id="parcelas-carne"
                    value={parcelasDoCarne}
                    onChange={(e) => setParcelasDoCarne(Number(e.target.value))}
                    style={{ width: 'auto', padding: '8px 10px', fontSize: 12 }}
                  >
                    {opcoesDeParcelas.map((n) => (
                      <option key={n} value={n}>
                        {n}x de {formatarBRL(Math.round(saldo / n))}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="primeiro-vencimento" style={{ margin: 0 }}>
                    1º vencimento
                  </label>
                  <input
                    id="primeiro-vencimento"
                    type="date"
                    value={primeiroVencimento}
                    min={hoje}
                    max={somarDias(hoje, MAX_DIAS_PRIMEIRO_VENCIMENTO)}
                    onChange={(e) => setPrimeiroVencimento(e.target.value)}
                    style={{ width: 'auto', padding: '8px 10px', fontSize: 12 }}
                  />
                  {/*
                    O operador escolhe uma data e recebe doze. Dizer em qual dia do mês elas
                    caem, e quando termina, é o que evita a descoberta no terceiro boleto —
                    principalmente do encurtamento de fevereiro, que ninguém espera.
                  */}
                  {problemaNaData ? (
                    <span className="aviso-desatualizado">⚠ {problemaNaData.motivo}</span>
                  ) : (
                    <span className="aguardando">
                      Vencem todo dia {partesDaData(primeiroVencimento)?.dia} ·{' '}
                      {formatarDataCurta(vencimentos[0]!)} a{' '}
                      {formatarDataCurta(vencimentos[vencimentos.length - 1]!)}
                    </span>
                  )}
                </>
              )}
            </>
          )}
          <BotaoDeCobranca
            cobranca={cobrancaDe('saldo')}
            rotulo="Gerar link do saldo"
            // Data inválida trava o botão: o servidor recusaria, e o operador descobriria
            // depois de esperar a ida ao gateway.
            ocupado={ocupado || (modalidadeDoSaldo === 'carne' && !!problemaNaData)}
            aoCobrar={() =>
              aoCobrar(
                'saldo',
                modalidadeDoSaldo,
                modalidadeDoSaldo === 'carne'
                  ? { parcelas: parcelasDoCarne, primeiroVencimento }
                  : undefined,
              )
            }
          />
        </>
      ) : null,
    },
    {
      nome: 'Onboarding',
      descricao:
        'Entrega do selo numerado e do cartão em cerimônia com o comitê e presença do fundador.',
      liberado: !!marcos.saldoPago,
      marco: 'onboardingRealizado',
      acoes: podeOperar ? (
        <button className="bt vazado pequeno" disabled={ocupado} onClick={aoRegistrarOnboarding}>
          Registrar onboarding
        </button>
      ) : null,
    },
  ];

  return (
    <div>
      {passos.map((passo, i) => (
        <div key={passo.nome} className={`passo ${passo.liberado ? '' : 'bloqueado'}`}>
          <div className="numero">{String(i + 1).padStart(2, '0')}</div>
          <div className="corpo">
            <div className="nome">{passo.nome}</div>
            <div className="descricao">{passo.descricao}</div>
            <div className="acoes">
              {passo.liberado ? (
                <>
                  {passo.acoes}
                  {marcos[passo.marco] && (
                    <span className="feito">
                      ✓ {new Date(marcos[passo.marco]!).toLocaleDateString('pt-BR')}
                    </span>
                  )}
                </>
              ) : (
                <span className="aguardando">Aguarda a etapa anterior</span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

interface Passo {
  nome: string;
  descricao: string;
  liberado: boolean;
  marco: MarcoId;
  acoes: React.ReactNode;
}

/**
 * Botão de emissão que conhece o estado do documento.
 *
 * Reemitir é legítimo enquanto ninguém assinou — corrigir um dado errado exige PDF novo, e
 * é para isso que existe o versionamento. Depois da assinatura, deixa de ser: o servidor
 * recusa, e a tela para de oferecer.
 */
function BotaoDeEmissao({
  documento,
  rotulo,
  ocupado,
  travadoPor,
  aoEmitir,
}: {
  documento: DocumentoNaFicha | null;
  rotulo: string;
  ocupado: boolean;
  travadoPor?: DocumentoNaFicha | null;
  aoEmitir: () => void;
}) {
  const assinado = documento?.assinadoRecebidoEm ?? travadoPor?.assinadoRecebidoEm;

  if (assinado) {
    return (
      <span className="arquivo-assinado">
        {rotulo} v{documento?.versao ?? '—'} assinado — não pode ser reemitido.
      </span>
    );
  }

  return (
    <button className="bt pequeno" disabled={ocupado} onClick={aoEmitir}>
      {ocupado
        ? 'Aguarde…'
        : documento
          ? `Reemitir ${rotulo} (v${documento.versao + 1})`
          : `Emitir ${rotulo}`}
    </button>
  );
}

/** Aviso de que o PDF emitido já não bate com a ficha. */
function AvisoDeDivergencia({ documento }: { documento: DocumentoNaFicha | null }) {
  if (!documento?.desatualizado) return null;
  return (
    <span className="aviso-desatualizado">
      ⚠ A ficha mudou depois desta emissão (v{documento.versao}) — reemita antes de assinar.
    </span>
  );
}

function BotaoDeAssinatura({
  documento,
  ocupado,
  aoRegistrar,
}: {
  documento: DocumentoNaFicha | null;
  ocupado: boolean;
  aoRegistrar: (documento: DocumentoNaFicha) => void;
}) {
  if (!documento) {
    return <span className="aguardando">Emita o documento primeiro.</span>;
  }

  if (documento.assinadoRecebidoEm) {
    return (
      <span className="arquivo-assinado">
        {DOCUMENTOS[documento.tipo].nome} v{documento.versao} assinado
        {documento.chaveS3Assinado ? ', com arquivo anexado' : ', sem arquivo anexado'}.
      </span>
    );
  }

  return (
    <button className="bt pequeno" disabled={ocupado} onClick={() => aoRegistrar(documento)}>
      Registrar assinatura
    </button>
  );
}

/**
 * Botão de cobrança que reflete o estado real do gateway.
 *
 * Oferecer "Gerar link" para uma cobrança já paga é oferecer uma ação impossível: o
 * servidor recusa com 409, e o operador descobre isso depois de clicar e esperar. A trava
 * de verdade continua sendo do servidor — esta aqui existe para a tela não mentir.
 */
function BotaoDeCobranca({
  cobranca,
  rotulo,
  ocupado,
  aoCobrar,
}: {
  cobranca: Cobranca | null;
  rotulo: string;
  ocupado: boolean;
  aoCobrar: () => void;
}) {
  if (cobranca && cobrancaJaPaga(cobranca)) {
    return (
      <>
        <span className="selo positivo">
          Pago · {formatarBRL(cobranca.valorRecebido ?? cobranca.valor)}
          {cobranca.valorRecebido != null && cobranca.valorRecebido !== cobranca.valor
            ? ' líquido'
            : ''}
        </span>
        <LinkDaCobranca cobranca={cobranca} />
      </>
    );
  }

  if (cobranca?.situacao === 'PENDENTE') {
    /*
     * Carnê pendente não é o mesmo que link não pago: pode ter três dos doze boletos já
     * liquidados. Mostrar só "aguardando pagamento" esconderia o dinheiro que já entrou.
     */
    const carne = cobranca.modalidade === 'carne' ? resumoDoCarne(cobranca) : null;

    return (
      <>
        <span className="selo atencao">
          {carne && carne.pagas > 0
            ? `Carnê · ${carne.pagas} de ${carne.total} pagas · ${formatarBRL(carne.recebido)} recebidos`
            : 'Link ativo · aguardando pagamento'}
        </span>
        <LinkDaCobranca cobranca={cobranca} />
        <button className="bt vazado pequeno" disabled={ocupado} onClick={aoCobrar}>
          Reenviar o mesmo link
        </button>
      </>
    );
  }

  return (
    <>
      <button className="bt pequeno" disabled={ocupado} onClick={aoCobrar}>
        {cobranca ? `${rotulo} (a anterior ${cobranca.situacao.toLowerCase()})` : rotulo}
      </button>
      <LinkDaCobranca cobranca={cobranca} />
    </>
  );
}

function LinkDaCobranca({ cobranca }: { cobranca: Cobranca | null }) {
  const [copiado, setCopiado] = useState(false);
  if (!cobranca) return null;

  return (
    <>
      <a
        href={cobranca.urlFatura}
        target="_blank"
        rel="noopener noreferrer"
        style={{ fontSize: 11 }}
      >
        Abrir fatura ({cobranca.situacao.toLowerCase()})
      </a>
      <button
        className="copiar"
        style={{ marginTop: 0 }}
        onClick={() => {
          void navigator.clipboard.writeText(cobranca.urlFatura).then(() => {
            setCopiado(true);
            setTimeout(() => setCopiado(false), 2000);
          });
        }}
      >
        {copiado ? 'Copiado' : 'Copiar link'}
      </button>
    </>
  );
}
